import Table, {
  buildActionColumn,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildExpandableCountColumn,
  TableControls
} from "@/components/Table";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import SearchInput from "@/components/SearchInput";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import { BackofficePerson } from "@/types";
import { useEffect, useMemo, useState } from "react";
import EmptyState from "@/components/EmptyState";
import { JobPost, ServiceLevel, isExecuSearchServiceLevel, PostStage, HiringStatus } from "../types";
import { toDateFormat } from "@/lib/dateHelpers";
import ApplicantDetailsDialog from "./ApplicantDetailsDialog";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import { useRequireCapability, useCanManageJobPostsBool } from "@/hooks/useCapabilities";
import { DOMAIN, PERMISSION } from "@/providers/AuthorizationProvider";
import { DELETE_JOB_APPLICATION, JOB_POST_WITH_APPLICATIONS_QUERY, updateCache } from "../queries";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";

import CreateApplicationReviewDialog from "./CreateApplicationReviewDialog";
import { useTopActionBar, SideBar } from "@/layouts/AppLayout/TopActionBarContext";
import CreateJobApplicationDialog from "./CreateJobApplicationDialog";
import { ensureProtocol, isDev } from "@/lib/urlUtils";
import { formatPhoneNumber } from "@/lib/phoneHelpers";
import CopyButton from "@/components/CopyButton";
import MetricGrid from "@/components/MetricGrid";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import { chain } from "lodash";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import { RowType } from "@/components/Table/Table";
import UpdateJobApplicationFilesModal from "@/components/UpdateJobApplicationFilesModal";
import { useJobPostContext } from "@/pages/_layouts/jobs/useJobPostContext";
import Icon from "@/components/Icon";
import { filterJobApplicationsBySearch } from "./jobApplicationsTableUtils";

export interface Applicant {
  applicant: BackofficePerson;
  email: string;
  phone: string;
  resume?: string;
  coverLetter?: string;
  website?: string;
  appliedOn: string;
  id: string;
  customApplicationData: CustomApplicationData;
}
export interface CustomApplicationData {
  currentOrMostRecentEmployer: string;
  currentOrMostRecentPosition: string;
  referralSource: {
    id: string;
    content: string;
  }[];
  id: string;
  answers: CustomApplicationAnswer[];
}

export interface CustomApplicationAnswer {
  selectedOption: {
    content: string;
  };
  customText: string;
  answerType: string;
  content: string;
  id: string;
  position: string;
  question: {
    id: string;
    content: string;
    position: number;
  };
}

interface ApplicationAnswerRow extends RowType {
  questionId: string;
  question: string;
  position: number;
  answer: string;
}

const formatAnswers = (answers: CustomApplicationAnswer[]) =>
  chain(answers)
    .groupBy((a) => a?.question?.id)
    .map((group) => ({
      questionId: group[0]?.question?.id,
      question: group[0]?.question?.content,
      position: group[0]?.question?.position,
      answer: group
        .map((a) => (a?.answerType === "custom_text" ? a?.customText : a?.selectedOption?.content))
        .join(", ")
    }))
    .sortBy("position")
    .value();

const buildAnswers = (row: Applicant) => {
  const { currentOrMostRecentPosition, currentOrMostRecentEmployer, referralSource, answers } =
    row.customApplicationData || {};
  const formattedAnswers = formatAnswers(answers);

  return [
    {
      question: "Current or most recent position",
      answer: currentOrMostRecentPosition
    },
    {
      question: "Current or most recent employer",
      answer: currentOrMostRecentEmployer
    },
    {
      question: "How did you learn about this job opportunity",
      answer: referralSource?.join(", ")
    },
    ...formattedAnswers
  ];
};

interface ViewJobApplicantsProps {
  jobPost: JobPost;
}

const getSalesforceJobPostUrl = (jobPostId: string) => {
  const baseUrl = isDev()
    ? "https://pgaservices--staging.sandbox.lightning.force.com/lightning/r/Job_Post__c"
    : "https://pgaservices.lightning.force.com/lightning/r/Job_Post__c";
  return `${baseUrl}/${jobPostId}/view`;
};

export default function ViewJobApplicants({ jobPost }: ViewJobApplicantsProps) {
  const { Menu, setMenu, reposition } = useTableRowMenu();
  const { isCompactLayout } = useIsCompactLayout();
  const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [metrics, setMetrics] = useState<MetricCardType<Path>[]>([]);
  const canManageJobPosts = useCanManageJobPostsBool();
  const { isJobFilledOrIncomplete } = useJobPostContext();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [applicantToDelete, setApplicantToDelete] = useState<Applicant | null>(null);
  const [applicants, setApplicants] = useState<any[]>([]);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [reviewApplicant, setReviewApplicant] = useState<Applicant | null>(null);
  const { showSidebar } = useTopActionBar();
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchValue, setSearchValue] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { data, loading, refetch, error } = useQuery<{ jobPost: JobPost }>(JOB_POST_WITH_APPLICATIONS_QUERY, {
    variables: { jobPostId: jobPost?.id },
    skip: !jobPost?.id,
    fetchPolicy: "cache-and-network"
  });

  const { cache } = useApolloClient();

  useEffect(() => {
    if (data) {
      setApplicants(data.jobPost.applications || []);
      updateCache(cache, data.jobPost.id, "applications", data.jobPost.applications);
    } else {
      setApplicants([]);
    }
  }, [cache, data]);

  useRequireCapability({
    domain: DOMAIN.JOBS,
    permission: PERMISSION.manageJobPosts
  });

  const [deleteJobApplication, { loading: isDeleting }] = useMutation(DELETE_JOB_APPLICATION, {
    onCompleted: (data) => {
      const result = data?.deleteJobApplication;
      if (!result?.success) {
        const message = result?.message;
        showErrorSnackbar("Application not deleted", {
          subtitle: message || "Please try again later."
        });
        return;
      }

      setIsDeleteModalOpen(false);
      setApplicantToDelete(null);
      refetch();
      showSnackbar(
        `Successfully deleted ${applicantToDelete?.applicant.firstName} ${applicantToDelete?.applicant.lastName}'s application`,
        { close: true }
      );
    },
    onError: () => {
      showErrorSnackbar("Application not deleted", { subtitle: "Please try again later." });
    }
  });

  const handleClickDelete = (applicant: Applicant) => {
    if (!applicant.id) {
      showErrorSnackbar("Application not deleted", { subtitle: "Application ID is missing." });
      return;
    }
    setApplicantToDelete({
      ...applicant,
      id: applicant.id
    });
    setIsDeleteModalOpen(true);
  };

  const handleDelete = () => {
    if (!applicantToDelete?.id) {
      showErrorSnackbar("Application not deleted", { subtitle: "Application ID is missing." });
      return;
    }

    deleteJobApplication({
      variables: {
        applicationId: applicantToDelete.id
      }
    });
  };

  const handleViewDetails = (applicant: Applicant) => {
    setSelectedApplicant(applicant);
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    setSelectedApplicant(null);
  };

  useEffect(() => {
    const displayedCount = applicants.length;

    setMetrics([
      {
        label: "Total Applicants",
        value: `${displayedCount}`,
        iconName: "business_center"
      },
      {
        label: "Days Remaining to Apply",
        value: `${Math.max(
          0,
          Math.round((new Date(jobPost.submissionDeadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
        )}`,
        iconName: "today"
      },
      {
        label: "Days Published",
        value: `${Math.round((new Date().getTime() - new Date(jobPost.createdAt).getTime()) / (1000 * 60 * 60 * 24))}`,
        iconName: "today"
      }
    ]);
  }, [applicants, jobPost.submissionDeadline, jobPost.createdAt]);

  const handleAddInteraction = (applicant: Applicant) => {
    showSidebar(SideBar.CREATE_GLOBAL_INTERACTION, {
      pgaId: applicant.applicant.pgaId,
      defaultJobPostId: jobPost.id
    })();
  };

  const handleAddReview = (applicant: Applicant) => {
    setReviewApplicant(applicant);
    setIsReviewDialogOpen(true);
  };

  const handleUpdateFiles = (applicant: Applicant) => {
    if (!applicant.id) {
      showErrorSnackbar("Application files not updated", { subtitle: "Application ID is missing." });
      return;
    }
    setSelectedApplicationId(applicant.id);
    setUpdateModalOpen(true);
  };

  const isFilledOrIncompleteJob = isJobFilledOrIncomplete(jobPost);

  const shouldDisplayCopyApplicantListLink =
    !isFilledOrIncompleteJob &&
    (jobPost.serviceLevel === "Advanced" || jobPost.serviceLevel === "Executive") &&
    jobPost.jobApplicantsListUrl;

  const shouldShowAddApplicant =
    (jobPost.stage as PostStage) !== PostStage.DRAFT && (jobPost.status as HiringStatus) !== HiringStatus.FILLED;

  const filteredApplicants = useMemo(
    () => filterJobApplicationsBySearch(applicants, searchValue),
    [applicants, searchValue]
  );

  const pageActions = useMemo((): Action[] => {
    const actions: Action[] = [];

    if (shouldShowAddApplicant) {
      actions.push({
        text: "Add Applicant",
        variant: "outlined",
        iconName: "add",
        testId: "add-applicant-button",
        onClick: () => setIsCreateDialogOpen(true)
      });
    }

    if (shouldDisplayCopyApplicantListLink) {
      actions.push({
        text: "Copy Applicant List Link",
        variant: "text",
        testId: "copy-applicant-list-link",
        copyButton: {
          textToCopy: jobPost.jobApplicantsListUrl || "",
          copyFieldLabel: "Applicant List Link"
        }
      });
    }

    return actions;
  }, [jobPost.jobApplicantsListUrl, shouldDisplayCopyApplicantListLink, shouldShowAddApplicant]);

  const isAdvancedOrExecutive = isExecuSearchServiceLevel(jobPost.serviceLevel);

  const getApplicantActions = (original: Applicant): Action[] => [
    {
      text: "Add Interaction",
      section: "Manage",
      onClick: () => {
        handleAddInteraction(original);
      }
    },
    ...(jobPost.serviceLevel !== "Self-Serve" && jobPost.serviceLevel !== "Assisted"
      ? [
          {
            text: "Add Review",
            section: "Manage",
            onClick: () => {
              handleAddReview(original);
            }
          } satisfies Action
        ]
      : []),
    ...(canManageJobPosts
      ? [
          {
            text: "Update Files",
            section: "Manage",
            onClick: () => {
              handleUpdateFiles(original);
            }
          } satisfies Action
        ]
      : []),
    {
      text: "Delete Application",
      iconName: "delete",
      destructive: true,
      onClick() {
        handleClickDelete(original);
      }
    }
  ];

  const allColumns: ColumnDef<Applicant>[] = [
    ...buildAvatarDetailsColumn<Applicant>({
      isCompactLayout,
      getTitle: (row) => `${row.applicant?.firstName || ""} ${row.applicant?.lastName || ""}`.trim(),
      getSubtitle: (row) => row.applicant?.latestProgram?.programType?.name || "",
      subtitleLabel: "Program",
      getPicture: (row) => row.applicant?.profilePhoto,
      header: "Applicant Name",
      size: 300,
      id: "name",
      sortType: "text"
    }),
    buildCopyTextColumn<Applicant>({
      accessorKey: "applicant.pgaId",
      header: "PGA ID",
      copyFieldLabel: "PGA ID",
      getText: (row) => row.applicant?.pgaId || "",
      shouldShowCopyButton: (row) => !!row.applicant?.pgaId,
      enableSorting: false
    }),
    {
      id: "section",
      header: "Section",
      accessorFn: (row) => row.applicant?.primarySection?.name || "",
      cell: ({ row }) => row.original?.applicant?.primarySection?.name || <span className="opacity-20">--</span>
    },
    {
      id: "email",
      header: "Email",
      accessorKey: "email",
      cell: ({ row }) => {
        const email = row.original.email;
        if (!email) {
          return <span className="opacity-20">--</span>;
        }

        return (
          <div className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
            <a href={`mailto:${email}`} className="text-sys-brand-primary hover:underline">
              {email}
            </a>
            <CopyButton textToCopy={email} copyFieldLabel="Email" />
          </div>
        );
      }
    },
    {
      id: "phone",
      header: "Phone",
      accessorKey: "phone",
      enableSorting: false,
      cell: ({ row }) => {
        const formattedPhone = formatPhoneNumber(row.original.phone);
        if (!formattedPhone) {
          return <span className="opacity-20">--</span>;
        }

        return (
          <div className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
            <a href={`tel:${row.original.phone}`} className="text-sys-brand-primary hover:underline">
              {formattedPhone}
            </a>
            <CopyButton textToCopy={formattedPhone} copyFieldLabel="Phone" />
          </div>
        );
      }
    },
    {
      id: "resume",
      header: "Resume",
      accessorKey: "resume",
      enableSorting: false,
      cell: ({ row }) =>
        row.original.resume ? (
          <a
            href={row.original.resume}
            className="text-sys-brand-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
            target="_blank"
            rel="noreferrer"
          >
            Resume
          </a>
        ) : (
          <span className="opacity-20">--</span>
        )
    },
    {
      id: "coverLetter",
      header: "Cover Letter",
      accessorKey: "coverLetter",
      cell: ({ row }) =>
        row.original.coverLetter ? (
          <a
            href={row.original.coverLetter}
            className="text-label-large text-sys-brand-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
            target="_blank"
            rel="noreferrer"
          >
            Cover Letter
          </a>
        ) : (
          <span className="opacity-20">--</span>
        )
    },
    {
      id: "website",
      header: "Website",
      accessorKey: "website",
      cell: ({ row }) =>
        row.original.website ? (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Icon name="open_in_new" className="text-sys-brand-primary" size={18} />
            <a
              href={ensureProtocol(row.original.website, false)}
              target="_blank"
              rel="noreferrer"
              className="text-sys-brand-primary text-label-large hover:underline"
            >
              Website
            </a>
          </div>
        ) : (
          <span className="opacity-20">--</span>
        )
    },
    ...(isAdvancedOrExecutive
      ? [
          buildExpandableCountColumn<Applicant>({
            id: "answers",
            accessorKey: "answers",
            header: "Answers",
            getCount: (row) => (row.customApplicationData ? buildAnswers(row).length : 0),
            getHasItems: (row) => !!row.customApplicationData,
            getDisplayText: (row, count) => (row.customApplicationData ? `${count}` : "--")
          })
        ]
      : []),
    {
      id: "appliedOn",
      header: "Applied On",
      accessorFn: (row) => row.appliedOn || "",
      cell: ({ row }) => (row.original.appliedOn ? toDateFormat(row.original.appliedOn, { tz: "utc" }) : "")
    },
    buildActionColumn<Applicant>({
      actionId,
      setMenu,
      getRowId: (row) => row.id,
      getActions: getApplicantActions,
      onViewClick: (row) => handleViewDetails(row),
      viewButtonTitle: "View applicant details"
    })
  ];

  const tableColumns: ColumnDef<Applicant>[] = isAdvancedOrExecutive
    ? allColumns.filter((column) => column.id !== "coverLetter" && column.id !== "website")
    : allColumns;

  const renderApplicantsNoDataEmptyState = () => {
    if (error) {
      return (
        <EmptyState
          title="Application Loading Issue"
          caption="Due to the high number of applications, this request is timing out. Please view applicants in Salesforce"
          iconName="error"
          action={{
            children: "Link to Salesforce Job Post",
            onClick: () => {
              window.open(getSalesforceJobPostUrl(jobPost.id), "_blank");
            },
            icon: "open_in_new"
          }}
        />
      );
    }

    if (
      jobPost.applyVia === "Facility Website" &&
      jobPost.serviceLevel !== ServiceLevel.ADVANCED &&
      jobPost.serviceLevel !== ServiceLevel.EXECUTIVE
    ) {
      return (
        <EmptyState
          title="Applications via Facility Website"
          caption="Applications for this job are submitted through the facility website and are not tracked here."
          iconName="person"
          action={{
            children: "Facility Website",
            onClick: () => {
              window.open(jobPost.externalJobUrl, "_blank");
            },
            icon: "open_in_new"
          }}
        />
      );
    }

    return <EmptyState title="No Applications" caption="There are no applications for this job." iconName="person" />;
  };

  const hasActiveSearch = !!searchValue.trim();

  return (
    <>
      <Menu />
      {!!metrics.length && <MetricGrid metrics={metrics} layout="expanded" />}
      <div className="pt-12" />
      <Table
        data={filteredApplicants}
        columns={tableColumns}
        loading={loading && !data}
        sortingState={sorting}
        onSortingChange={setSorting}
        onHorizontalScroll={reposition}
        hasActiveFilters={hasActiveSearch}
        getRowCanExpand={(row) => isAdvancedOrExecutive && !!row.original.customApplicationData}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls actions={pageActions} onSortClick={onSortClick}>
            <SearchInput
              placeholder="Search Applicants"
              query={searchValue}
              onChange={setSearchValue}
              onClear={() => setSearchValue("")}
            />
          </TableControls>
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search." iconName="search" />
        )}
        renderNoDataEmptyState={renderApplicantsNoDataEmptyState}
        renderExpandedRow={({ row }) => {
          const answers = buildAnswers(row.original) as ApplicationAnswerRow[];
          return (
            <TableDetailsPanel
              title={`Answers (${answers.length})`}
              data={answers}
              columns={[
                {
                  accessorKey: "question",
                  header: "Question"
                },
                {
                  accessorKey: "answer",
                  header: "Answer"
                }
              ]}
            />
          );
        }}
        onClickRow={(row) => {
          handleViewDetails(row.original);
        }}
      />
      <ApplicantDetailsDialog
        applicant={selectedApplicant ? selectedApplicant.applicant : null}
        applicationEmail={selectedApplicant?.email}
        applicationPhone={selectedApplicant?.phone}
        open={isSidebarOpen}
        onClose={handleCloseSidebar}
      />
      <CreateApplicationReviewDialog
        open={isReviewDialogOpen}
        onClose={() => {
          setIsReviewDialogOpen(false);
          setReviewApplicant(null);
        }}
        jobApplicationId={reviewApplicant?.id || ""}
        applicantName={
          reviewApplicant ? `${reviewApplicant.applicant.firstName} ${reviewApplicant.applicant.lastName}` : ""
        }
        isRecommendation={false}
        onSuccess={refetch}
      />
      <Dialog
        open={isDeleteModalOpen}
        onClosed={() => setIsDeleteModalOpen(false)}
        headline="Delete Application"
        className="w-[490px] p-6 pr-2"
        actions={
          <div className="flex gap-2">
            <Button variant="text" onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="filled"
              theme="error"
              trailingIcon={false}
              icon="delete"
              onClick={handleDelete}
              disabled={isDeleting || !applicantToDelete?.id}
              data-testid="confirm-delete-application"
            >
              Delete
            </Button>
          </div>
        }
      >
        <p>
          Are you sure you want to delete this application? This will remove {applicantToDelete?.applicant.firstName}{" "}
          {applicantToDelete?.applicant.lastName}&apos;s application. This cannot be undone.
        </p>
      </Dialog>
      {updateModalOpen && selectedApplicationId && (
        <UpdateJobApplicationFilesModal
          isOpen={updateModalOpen}
          onClose={() => {
            setUpdateModalOpen(false);
            setSelectedApplicationId(null);
          }}
          applicationId={selectedApplicationId}
          onSuccess={() => {
            setUpdateModalOpen(false);
            setSelectedApplicationId(null);
            refetch();
          }}
        />
      )}
      <CreateJobApplicationDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        jobPost={jobPost}
        onSuccess={refetch}
      />
    </>
  );
}
